import React, { useId } from "react";
import "./Header.css";

/**
 * Header
 * ------
 * Hero-style header for the leaderboard/landing screens.
 *
 * Behaviour:
 * - Shows a looping intro video alongside event text.
 * - Falls back to "Gemba Advantage" when `eventName` is empty.
 * - Renders an optional list of paragraph snippets under the headings.
 */

// TYPES -------------------------------------------------------------
type Props = {
  /** Optional event name; falls back to "Gemba Advantage" if empty/whitespace. */
  eventName?: string;

  /** Optional paragraphs rendered beneath the headings. */
  content?: Array<string | React.ReactNode>;

  /** Optional extra class on the outer wrapper. */
  className?: string;
};

// COMPONENT ---------------------------------------------------------
const Header: React.FC<Props> = ({ content = [], eventName, className = "" }) => {
  /** IDS ----------------------------------------------------------- */
  // Used to programmatically label the header region
  const headingId = useId();

  /** DERIVED ------------------------------------------------------- */
  const displayEventName = eventName?.trim() ? eventName : "Gemba Advantage";

  /** RENDER -------------------------------------------------------- */
  return (
    <header
      className={`header ${className}`.trim()}
      role="region"
      aria-labelledby={headingId}
    >
      <div className="header-main">
        {/* Decorative looped robot video (hidden from SRs) */}
        <div className="intro-video" aria-hidden="true">
          <video
            autoPlay
            loop
            muted
            playsInline
            preload="auto"
            src="/assets/video/Robot-Character-Circle.webm"
          />
        </div>

        {/* Textual content */}
        <div className="header-text">
          <p className="event-label">Welcome to the ...</p>

          {/* Main heading labels the region for SRs */}
          <h1 id={headingId}>{displayEventName}</h1>
          <h2>Digital Escape Rooms</h2>

          {/* Additional paragraphs/snippets */}
          {content.map((p, index) => (
            <p key={index}>{p}</p>
          ))}
        </div>
      </div>
    </header>
  );
};

export default Header;
